import React, { forwardRef, ForwardRefRenderFunction, ReactNode, useContext } from 'react'

import { Box, ButtonV2, Divider, Text } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import ProductionListContext from '@/context/productions-list'
import { trackEvent } from '@/utils/analytics/ga360'

import styles from './styles.module.scss'

type Props = {
    className?: string
    containerClassName?: string
    isSticky?: boolean
    withReset?: boolean
    hideWhenNoResults?: boolean
    children?: ReactNode
    customFilterReset?: () => void
}

const FilterBar: ForwardRefRenderFunction<HTMLDivElement, Props> = (
    { className, containerClassName, children, isSticky, withReset = true, hideWhenNoResults, customFilterReset },
    ref,
) => {
    const {
        dispatch: { onFilterResetAction },
        selectors: { hasFiltersApplied, productions },
    } = useContext(ProductionListContext)

    if (!hasFiltersApplied && productions.length === 0 && hideWhenNoResults) return null

    const resetEnabled: boolean = hasFiltersApplied

    const mergedClassName = classNames(styles.container, containerClassName, {
        [styles.isSticky]: isSticky,
    })

    const handleReset = () => {
        if (customFilterReset) {
            customFilterReset()
        } else {
            onFilterResetAction()
        }
        trackEvent('FilterUse', 'Clear All Filters Button Clicked')
    }

    return (
        <Box className={className} flexGrow>
            <div ref={ref} className={mergedClassName} data-testid="filter-bar-inner">
                {children}
                {resetEnabled && withReset && (
                    <ButtonV2 onClick={handleReset} importance="text" data-testid="btn-filter-bar-reset">
                        <Text size="lg" weight="semi-bold" altFont>
                            Reset
                        </Text>
                    </ButtonV2>
                )}
            </div>
            <Box mobileDisplay="none" tabletDisplay="block">
                <Divider />
            </Box>
        </Box>
    )
}

FilterBar.displayName = 'FilterBar'

export default forwardRef(FilterBar)
